import React, { useState } from "react";
import { Button, Typography, Box, Grid, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Success from "../../../ATS/images/confetti.gif";
import OfferLetterDocumentEditor, {
  generateOfferLetterPdf,
} from "./editor/OfferLetterDocumentEditor";
import { useDispatch, useSelector } from "react-redux";
import { GetOfferLetterTemplateAction } from "../../../../../store/ATS/FormTemplate/Action";

const Preview = ({
  setCurrentMode,
  currentMode,
  activeStep,
  setActiveStep,
  data,
  letterHeadData, 
  companylogo,
  isStepLoading,
  setIsStepLoading,
}) => {
  const dispatch = useDispatch();
  const { letterHeadData: reduxLetterHead } = useSelector(
    (state) => state?.AncillaryReducer?.LetterHeadReducer || {}
  );
  const currentLetterHead = letterHeadData || (Array.isArray(reduxLetterHead) ? reduxLetterHead[0] : reduxLetterHead);
  const [step, setStep] = useState("1");
  const [downloading, setDownloading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const previewEditorRef = React.useRef(null);

  const updateLoading = (val) => {
    setCompleting(val);
    if (setIsStepLoading) setIsStepLoading(val);
  };
  
  const onSubmit = async () => {
    try {
      updateLoading(true);
      await dispatch(GetOfferLetterTemplateAction());
      setStep("2");
      setTimeout(() => {
        updateLoading(false);
        setCurrentMode("grid");
      }, 3000);
    } catch (err) {
      console.error("Error completing offer letter:", err);
      updateLoading(false);
    }
  };

  const isLetterheadMode = Boolean(
    data?.header_footer_mode === "LETTERHEAD" ||
    (data?.is_letterhead === true && data?.header_footer_mode !== "OUR_HEADER_FOOTER")
  );

  const effectiveHeader = isLetterheadMode ? "" : (data?.header || currentLetterHead?.letterhead_header || "");
  const effectiveFooter = isLetterheadMode ? "" : (data?.footer || currentLetterHead?.letterhead_footer || "");

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      // Give React an immediate paint cycle so button spinner renders smoothly before background work begins
      await new Promise((resolve) => setTimeout(resolve, 40));

      const activeDocBytes = data?.docBytes || previewEditorRef.current?.getDocBytes?.() || null;

      const pdf = await generateOfferLetterPdf({
        content: data?.offer_letter_content,
        docBytes: activeDocBytes,
        isLetterhead: isLetterheadMode,
        header_footer_mode: isLetterheadMode ? "LETTERHEAD" : "OUR_HEADER_FOOTER",
        letterHeadData: isLetterheadMode ? currentLetterHead : null,
        companylogo: isLetterheadMode ? null : companylogo,
        headerTitle: effectiveHeader,
        footerText: effectiveFooter,
        payConfigContent: "",
        fieldValues: previewEditorRef.current?.getPreviewValues?.() || null,
        watermark: "PREVIEW",
      });

      pdf.save(`${data?.offer_letter_template_name || "Offer_Letter_Template"}.pdf`);
    } catch (err) {
      console.error("Error generating preview PDF:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {step === "1" ? (
        <>
          <Grid container justifyContent="center" direction="column" alignItems="center">
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                  color: "#166534",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "#16a34a", flexShrink: 0 }} />
                <span>
                  <strong>Note:</strong> The values shown below are for preview purposes only and will be replaced with actual candidate salary figures upon offer letter preparation.
                </span>
              </Box>

              <OfferLetterDocumentEditor
                ref={previewEditorRef}
                value={data?.offer_letter_content}
                docBytes={data?.docBytes}
                header_footer_mode={data?.header_footer_mode || (data?.is_letterhead ? "LETTERHEAD" : "OUR_HEADER_FOOTER")}
                is_letterhead={data?.is_letterhead}
                letterHeadData={currentLetterHead}
                companylogo={companylogo}
                headerTitle={effectiveHeader}
                footerText={effectiveFooter}
                mode="view"
                watermark="PREVIEW"
                isStepLoading={isStepLoading || downloading || completing}
              />
            </Box>

            <Grid item xs={12} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "6px",
                  padding: "0px 10px 4px 10px",
                }}
              >
                <Button
                  variant="contained"
                  className="mpl-primary-btn"
                  onClick={() => setActiveStep(activeStep - 1)}
                  disabled={downloading || completing}
                >
                  Back
                </Button>

                <Button
                  variant="outlined"
                  sx={{
                    minWidth: "190px",
                    height: "38px",
                    borderColor: "#8b44ff",
                    color: "#8b44ff",
                    fontWeight: 600,
                    textTransform: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#7c3aed",
                      backgroundColor: "rgba(139, 68, 255, 0.04)",
                    },
                  }}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={16} sx={{ color: "#8b44ff" }} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadPdf}
                  disabled={downloading || completing}
                >
                  {downloading ? "Generating PDF..." : "Download for Preview"}
                </Button>

                <Button
                  variant="contained"
                  className="mpl-primary-btn"
                  onClick={onSubmit}
                  disabled={downloading || completing}
                  startIcon={
                    completing ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  Complete Offer Letter
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              maxWidth: "600px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#e6f4ea",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              borderRadius: "10px",
              padding: "20px",
              margin: "auto",
            }}
          >
            <img
              src={Success}
              alt="Success"
              width={"100px"}
              height={"100px"}
              style={{
                backgroundColor: "#fff",
                borderRadius: "50%",
                padding: "10px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
              }}
            />
            <Typography sx={{ pt: 1, pb: 1, textAlign: "center" }} variant="h4">
              {currentMode === "Edit"
                ? " Your Offer Letter Template is Updated Successfully"
                : " Your Offer Letter Template is Created Successfully"}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default Preview;
